import {
    PLATFORM_SECTIONS,
    listPlatformProducts,
    buildPlatformSnapshot,
} from '../platform/product-registry.js';
import {composeWeatherBriefing} from '../lib/weather-briefing.js';

const SUMMARY_SECTION = Object.freeze({
    id: 'summary',
    label: 'Sintesi',
    description: 'Quadro operativo unificato di ciò che accade e di ciò che va seguito.',
    order: 0,
});

const TAB_LABELS = Object.freeze({
    summary: 'Sintesi',
    live: 'Live',
    forecast: 'Forecast',
    tracking: 'Tracking',
    vision: 'Vision AI',
    system: 'Sistemi',
});

const SECTION_ICONS = Object.freeze({
    summary: '◆',
    live: '●',
    forecast: '↗',
    tracking: '⌁',
    vision: '◉',
    system: '⚙',
});

const SECTION_COLORS = Object.freeze({
    live: '#20a86b',
    forecast: '#087fc1',
    tracking: '#e17a29',
    vision: '#8b63e6',
    system: '#557189',
});

const SNAPSHOT_MODES = new Set(['live', 'nowcast', 'forecast', 'all']);
const mounts = new WeakMap();
let activeWorkspace = null;
let workspaceSequence = 0;

function platformSections() {
    return Object.values(PLATFORM_SECTIONS).slice().sort((a, b) => a.order - b.order);
}

function createElement(documentRef, tag, className, text) {
    const node = documentRef.createElement(tag);
    if (className) node.className = className;
    if (text !== undefined && text !== null) node.textContent = String(text);
    return node;
}

function finite(value, fallback = null) {
    const number = Number(value);
    return Number.isFinite(number) ? number : fallback;
}

function normalizeTargetMs(value) {
    const number = finite(value);
    if (number === null) return null;
    return Math.abs(number) < 1e11 ? number * 1000 : number;
}

function normalizeSnapshotMode(mode, targetMs) {
    const raw = String(mode || '').trim().toLowerCase();
    if (SNAPSHOT_MODES.has(raw)) return raw;
    if (['history', 'historical', 'past', 'archive'].includes(raw)) return 'all';
    if (['future', 'prediction', 'scenario'].includes(raw)) return 'forecast';
    if (['now', 'realtime', 'real-time'].includes(raw)) return 'live';

    const target = normalizeTargetMs(targetMs);
    if (target !== null && target > Date.now() + 5 * 60 * 1000) return 'forecast';
    return 'live';
}

function escapeMarkup(value) {
    return String(value ?? '')
        .replaceAll('&', '&amp;')
        .replaceAll('<', '&lt;')
        .replaceAll('>', '&gt;')
        .replaceAll('"', '&quot;')
        .replaceAll("'", '&#39;');
}

function markupNode(documentRef, markup) {
    const template = documentRef.createElement('template');
    template.innerHTML = String(markup || '').trim();
    return template.content.firstElementChild;
}

function checked(value) {
    return value ? ' checked' : '';
}

function selected(value, expected) {
    return value === expected ? ' selected' : '';
}

/**
 * The operational controls have one renderer and one stable DOM contract.
 * Domain code supplies values only; this module owns every panel node.
 */
export function buildOperationalWorkspaceMarkup(content = {}) {
    const sourceCount = Number(content.sourceCount) || 0;
    const freshSourceCount = Number(content.freshSourceCount) || 0;
    const modes = [
        ['wind', '🌬️', 'Vento'],
        ['pressure', '◎', 'Pressione'],
        ['satellite', '🛰️', 'Satellite'],
        ['events', '✦', 'Fenomeni'],
    ].map(([mode, icon, label]) =>
        `<button data-mode="${mode}" class="${content.mode === mode ? 'active' : ''}">${icon}<b>${label}</b></button>`
    ).join('');
    const attention = (Array.isArray(content.attention) ? content.attention : []).slice(0, 6);
    const facts = attention.length
        ? attention.map(item => {
            const sources = (Array.isArray(item.sources) ? item.sources : [])
                .map(source => escapeMarkup(source?.label)).filter(Boolean).join(' + ');
            const areas = Number(item.areaCount) > 1 ? ` · ${Number(item.areaCount)} aree` : '';
            return `<button class="wsi-fact" data-lat="${escapeMarkup(item.lat)}" data-lon="${escapeMarkup(item.lon)}"><i style="--c:${escapeMarkup(item.color)}"></i><span><b>${escapeMarkup(`${item.icon || ''} ${item.title || ''}`.trim())}${areas}</b><small>${escapeMarkup(item.summary)}</small><em>${sources}</em></span><strong>${escapeMarkup(item.trust)}</strong></button>`;
        }).join('')
        : '<p>Nessun fenomeno operativo da seguire.</p>';
    const wsnow = content.wsnow || {};
    const wsnowKpis = (Array.isArray(wsnow.kpis) ? wsnow.kpis : []).map(kpi =>
        `<span><b>${escapeMarkup(kpi?.value)}</b><small>${escapeMarkup(kpi?.label)}</small></span>`
    ).join('');
    const wsnowFooter = wsnow.footer ? `<footer>${escapeMarkup(wsnow.footer)}</footer>` : '';
    const technicalDetails = (Array.isArray(content.technicalDetails) ? content.technicalDetails : [])
        .map(section => `<details class="wsi-more"><summary>${escapeMarkup(section?.summary)}</summary>${String(section?.trustedHtml || '')}</details>`);
    const visible = content.visible || {};
    const shadeMode = String(content.shadeMode || 'off');

    return Object.freeze({
        now: `<section class="wsi-now"><b>Cosa sta accadendo</b><p>${escapeMarkup(content.headline)}</p></section>`,
        statusline: `<div class="wsi-statusline"><span class="ok">● Operativo</span><b>${freshSourceCount}/${sourceCount} fonti live</b><span id="wsi-display-trust" class="wsi-display-trust pending">Verifica display…</span><small>aggiornato ${escapeMarkup(content.updatedLabel || 'n/d')}</small></div>`,
        wsnow: `<section class="wsi-v3-status ${wsnow.operational ? 'is-safe' : 'is-degraded'}" aria-live="polite"><header><b>WS NOW V3</b><span>${escapeMarkup(wsnow.status || 'NON DISPONIBILE')}</span></header><p>${escapeMarkup(wsnow.description || 'Lo stato operativo V3 non è disponibile; nessun altro motore viene usato come fallback.')}</p>${wsnowKpis ? `<div class="wsi-v3-kpis">${wsnowKpis}</div>` : ''}${wsnowFooter}</section>`,
        modes: `<nav class="wsi-modes" aria-label="Vista meteo">${modes}</nav>`,
        nearMe: '<button class="wsi-here" id="wsi-near-me">📍 Dimmi cosa accade intorno a me</button>',
        timeline: `<div class="wsi-timeline"><input id="wsi-time" type="range" min="0" max="${Math.max(0, Number(content.timelineMax) || 0)}" value="${Math.max(0, Number(content.timelineValue) || 0)}"><small id="wsi-time-label"></small></div>`,
        local: '<div class="wsi-local" id="wsi-local" hidden></div>',
        attention: `<section class="wsi-attention"><header><b>Da seguire</b><span>${Number(content.attentionCount) || 0}</span></header><div class="wsi-facts">${facts}</div></section>`,
        mapOptions: `<details class="wsi-more wsi-map-options"><summary>Personalizza la mappa</summary><div class="wsi-actions"><button id="wsi-sicily">Sicilia</button><button id="wsi-europe">Europa</button><button id="wsi-clean">Ripristina</button><label>Colori <select id="wsi-shade-mode"><option value="off"${selected(shadeMode, 'off')}>Nessuno</option><option value="wind"${selected(shadeMode, 'wind')}>Vento 10 m</option><option value="pressure"${selected(shadeMode, 'pressure')}>Pressione</option><option value="t850"${selected(shadeMode, 't850')}>Massa d’aria 850 hPa</option></select></label><label><input id="wsi-isobar" type="checkbox"${checked(visible.isobars)}> Isobare</label><label><input id="wsi-wind" type="checkbox"${checked(visible.wind)}> Scie vento</label><label><input id="wsi-advanced" type="checkbox"${checked(visible.advanced)}> Fronti</label><label><input id="wsi-territorial" type="checkbox"${checked(visible.territorial)}> Analisi locali</label><label><input id="wsi-centers" type="checkbox"${checked(visible.centers)}> Centri A/B/C</label><label><input id="wsi-tracks" type="checkbox"${checked(visible.tracks)}> Traiettorie A/B/C</label><label><input id="wsi-satellite" type="checkbox"> Nubi reali</label><label><input id="wsi-t850" type="checkbox"${checked(shadeMode === 't850')}> Masse d’aria</label><label><input id="wsi-insights" type="checkbox"${checked(visible.insights)}> Eventi</label></div><div class="wsi-pressure-legend"><div><b>Velocità vento a 10 m</b><small>ECMWF · km/h</small></div><i></i><div class="wsi-pressure-legend__ticks"><span>0</span><span>40</span><span>90+</span></div><p>Colore = velocità · scie = direzione</p></div></details>`,
        technicalDetails,
        footer: `<footer>${escapeMarkup(content.footer || 'Ogni valutazione mantiene fonti, tempo e confidenza verificabili')}</footer>`,
    });
}

function controlElement(record, target) {
    return record.document.getElementById(String(target || ''));
}

function readControlValue(record, target) {
    const element = controlElement(record, target);
    if (!element) return undefined;
    if ('checked' in element) return Boolean(element.checked);
    if ('value' in element && element.value !== undefined) return element.value;
    const pressed = element.getAttribute('aria-pressed');
    if (pressed !== null) return pressed === 'true';
    return element.classList.contains('active') || undefined;
}

function collectActiveControls(record) {
    const controls = {};
    for (const product of listPlatformProducts()) {
        const descriptors = (product.actions || []).filter(action => action.type === 'control');
        const booleanControls = descriptors.filter(action => typeof action.value === 'boolean');
        const booleanActive = !booleanControls.length
            || booleanControls.some(action => readControlValue(record, action.target) === action.value);
        for (const descriptor of descriptors) {
            if (descriptor.value === 'selected' && !booleanActive) continue;
            const value = readControlValue(record, descriptor.target);
            if (value !== undefined) controls[descriptor.target] = value;
        }
    }
    return controls;
}

function safeSnapshot(record) {
    const mode = normalizeSnapshotMode(record.context.mode, record.context.targetMs);
    return buildPlatformSnapshot(record.context.state || {}, {
        mode,
        activeControls: collectActiveControls(record),
    });
}

function formatNumber(value) {
    const number = finite(value);
    if (number === null) return null;
    return new Intl.NumberFormat('it-IT', {maximumFractionDigits: 1}).format(number);
}

function metricLabel(product) {
    const metrics = product.metrics || {};
    const freshSources = finite(metrics.freshSourceCount);
    const sourceCount = finite(metrics.sourceCount);
    if (freshSources !== null && sourceCount !== null) {
        return formatNumber(freshSources) + '/' + formatNumber(sourceCount) + ' fonti';
    }

    const candidates = [
        ['freshCount', ' live'],
        ['frameCount', ' frame'],
        ['count1h', ' in 1h'],
        ['activeCellCount', ' celle'],
        ['eventCount', ' eventi'],
        ['insightCount', ' evidenze'],
        ['localityCount', ' località'],
        ['timestepCount', ' step'],
        ['trackCount', ' tracce'],
        ['cameraObservationCount', ' webcam'],
        ['observationCount', ' osservazioni'],
        ['usableObservationCount', ' utilizzabili'],
        ['disabledMetricCount', ' esclusi'],
        ['earthquakeCount24h', ' sisma 24h'],
    ];
    for (const [key, suffix] of candidates) {
        const value = finite(metrics[key]);
        if (value !== null) return formatNumber(value) + suffix;
    }

    const leadMinutes = finite(metrics.maxLeadMinutes);
    if (leadMinutes !== null) return '+' + formatNumber(leadMinutes) + ' min';
    const horizonHours = finite(
        metrics.servedHorizonHours
        ?? metrics.horizonHours
        ?? metrics.forecastHorizonHours,
    );
    if (horizonHours !== null) return '+' + formatNumber(horizonHours) + ' h';
    const primaryModel = metrics.primaryModel || metrics.engine || metrics.etnaState;
    return primaryModel ? String(primaryModel) : (product.available ? 'disponibile' : 'da verificare');
}

function productIcon(product) {
    const id = product.id || '';
    const icons = [
        ['stations', '🌡️'],
        ['radar', '🌧️'],
        ['satellite', '🛰️'],
        ['lightning', '⚡'],
        ['hail', '🧊'],
        ['storm', '⛈️'],
        ['nowcast', '☔'],
        ['localities', '📍'],
        ['model-fields', '🗺️'],
        ['physics', '∿'],
        ['synoptic', '◎'],
        ['fronts', '⌁'],
        ['territorial', '✦'],
        ['webcam', '📷'],
        ['weather-state', '◈'],
        ['alerts', '⚠️'],
        ['geophysical', '🌋'],
        ['quality', '✓'],
        ['fusion', 'WS'],
    ];
    return icons.find(([needle]) => id.includes(needle))?.[1] || SECTION_ICONS[product.section] || '•';
}

function normalizedSearch(value) {
    return String(value || '')
        .normalize('NFD')
        .replace(/[\u0300-\u036f]/g, '')
        .toLocaleLowerCase('it-IT')
        .trim();
}

function humanReason(reason) {
    return String(reason || '').replaceAll('_', ' ');
}

function productPresentation(product) {
    const degradedStatuses = new Set(['degraded', 'error', 'unavailable', 'unknown']);
    const degraded = degradedStatuses.has(product.status)
        || degradedStatuses.has(product.sourceStatus)
        || (!product.available && !product.temporal?.locked);
    const locked = product.temporal?.locked === true;
    const badges = [];
    if (locked) badges.push({label: 'Blocco temporale', title: humanReason(product.temporal.reason)});
    if (degraded) badges.push({label: 'Degradato', title: humanReason(product.reason)});
    if (!badges.length) badges.push({label: 'Operativo', title: 'Prodotto disponibile nel contesto corrente'});
    return {degraded, locked, badges};
}

function actionLabel(product) {
    if (product.temporal?.locked) return 'Non disponibile a questo tempo';
    const hasControl = (product.actions || []).some(action => action.type === 'control');
    const requested = product.requestedActive || product.active;
    if (hasControl) return requested ? 'Disattiva dalla mappa' : 'Mostra nella mappa';
    if ((product.actions || []).some(action => action.type === 'timeline')) return 'Apri sulla timeline';
    if ((product.actions || []).some(action => action.type === 'mode')) return 'Apri la vista';
    return 'Apri il prodotto';
}

function dispatchWorkspaceEvent(record, type, descriptor, product) {
    const EventCtor = record.document.defaultView?.CustomEvent || globalThis.CustomEvent;
    if (!EventCtor) return;
    record.document.dispatchEvent(new EventCtor('wsv2:platform-' + type + '-request', {
        detail: {
            descriptor,
            productId: product?.id || null,
            section: product?.section || null,
            mode: record.snapshot?.mode || null,
        },
    }));
}

function callBridge(record, name, value, descriptor, product) {
    const callback = record.callbacks[name];
    if (typeof callback === 'function') {
        try {
            callback(value, descriptor, product);
        } catch (error) {
            console.error('[WS Intelligence workspace] ' + name + ' callback failed', error);
        }
    }
}

function selectWorkspaceSection(record, sectionId, options = {}) {
    if (!record.sectionUi.has(sectionId)) return false;
    record.activeSection = sectionId;
    setExpertOpen(record, sectionId !== 'summary', {focus: false});
    for (const [id, ui] of record.sectionUi) {
        const active = id === sectionId;
        ui.tab.setAttribute('aria-selected', active ? 'true' : 'false');
        ui.tab.tabIndex = active ? 0 : -1;
        ui.view.hidden = !active;
    }
    if (options.focus) record.sectionUi.get(sectionId).tab.focus();
    if (options.notify !== false) {
        const descriptor = {
            type: 'section',
            target: 'workspace',
            value: sectionId,
            source: options.source || 'tab',
        };
        callBridge(record, 'onSection', sectionId, descriptor, null);
        dispatchWorkspaceEvent(record, 'section', descriptor, null);
    }
    return true;
}

function internalSectionForAction(descriptor) {
    if (descriptor.target === 'wsi-panel') {
        if (['summary', 'local'].includes(descriptor.value)) return 'summary';
        if (descriptor.value === 'physics') return 'forecast';
        if (descriptor.value === 'reliability') return 'system';
    }
    if (descriptor.target === 'webcam-popup') return 'vision';
    if (platformSections().some(section => section.id === descriptor.value)) return descriptor.value;
    return null;
}

function runControlAction(record, descriptor, activating) {
    const element = controlElement(record, descriptor.target);
    if (!element) return false;

    const EventCtor = record.document.defaultView?.Event || globalThis.Event;

    if ('checked' in element) {
        const desired = typeof descriptor.value === 'boolean'
            ? (activating ? descriptor.value : !descriptor.value)
            : !element.checked;
        if (element.checked !== desired) {
            element.checked = desired;
            element.dispatchEvent(new EventCtor('change', {bubbles: true}));
        }
        return true;
    }

    if (element.matches('button, [role="button"]') || descriptor.value === 'activate') {
        element.click();
        return true;
    }

    if ('value' in element) {
        if (!activating && descriptor.value === 'selected') return true;
        if (descriptor.value !== 'selected' && descriptor.value !== null) {
            element.value = String(descriptor.value);
        }
        element.dispatchEvent(new EventCtor('change', {bubbles: true}));
        return true;
    }

    element.dispatchEvent(new EventCtor('change', {bubbles: true}));
    return true;
}

function runDescriptor(record, descriptor, product, activating) {
    if (descriptor.type === 'control') {
        runControlAction(record, descriptor, activating);
        dispatchWorkspaceEvent(record, 'control', descriptor, product);
        return;
    }
    if (!activating) return;

    if (descriptor.type === 'mode') {
        const button = Array.from(record.panel.querySelectorAll('.wsi-modes button[data-mode]'))
            .find(candidate => candidate.dataset.mode === String(descriptor.value));
        if (button) button.click();
        else callBridge(record, 'onMode', descriptor.value, descriptor, product);
        dispatchWorkspaceEvent(record, 'mode', descriptor, product);
        return;
    }

    if (descriptor.type === 'timeline') {
        callBridge(record, 'onTimeline', descriptor.value, descriptor, product);
        dispatchWorkspaceEvent(record, 'timeline', descriptor, product);
        return;
    }

    if (descriptor.type === 'section') {
        const internal = internalSectionForAction(descriptor);
        if (internal) selectWorkspaceSection(record, internal, {
            source: 'product',
            notify: false,
        });
        callBridge(record, 'onSection', descriptor.value, descriptor, product);
        dispatchWorkspaceEvent(record, 'section', descriptor, product);
    }
}

function runProductAction(record, product) {
    if (product.temporal?.locked) return;
    const controls = (product.actions || []).filter(action => action.type === 'control');
    const requested = product.requestedActive || product.active;
    const activating = controls.length ? !requested : true;
    for (const descriptor of product.actions || []) {
        runDescriptor(record, descriptor, product, activating);
    }
    queueMicrotask(() => {
        if (record.panel.isConnected) renderWorkspace(record);
    });
}

function createProductCard(record, product) {
    const documentRef = record.document;
    const presentation = productPresentation(product);
    const card = createElement(documentRef, 'article', 'wsp-product');
    card.dataset.productId = product.id;
    const requested = product.requestedActive || product.active;
    card.classList.toggle('is-active', Boolean(requested));
    card.classList.toggle('is-degraded', presentation.degraded);
    card.classList.toggle('is-locked', presentation.locked);
    card.style.setProperty('--product-color', SECTION_COLORS[product.section] || '#087fc1');

    const icon = createElement(documentRef, 'span', 'wsp-product__icon', productIcon(product));
    icon.setAttribute('aria-hidden', 'true');
    const copy = createElement(documentRef, 'div', 'wsp-product__copy');
    copy.append(
        createElement(documentRef, 'b', '', product.title),
        createElement(documentRef, 'p', '', product.description),
    );

    const meta = createElement(documentRef, 'div', 'wsp-product__meta');
    for (const badge of presentation.badges) {
        const state = createElement(documentRef, 'span', 'wsp-product__state', badge.label);
        if (badge.title) state.title = badge.title;
        meta.appendChild(state);
    }
    meta.appendChild(createElement(documentRef, 'span', 'wsp-product__metric', metricLabel(product)));

    const actionButton = createElement(
        documentRef,
        'button',
        'wsp-product__action',
        actionLabel(product),
    );
    actionButton.type = 'button';
    actionButton.disabled = presentation.locked;
    actionButton.setAttribute('aria-pressed', requested ? 'true' : 'false');
    actionButton.setAttribute('aria-label', actionLabel(product) + ': ' + product.title);
    if (presentation.locked) actionButton.setAttribute('aria-disabled', 'true');
    actionButton.addEventListener('click', () => runProductAction(record, product));

    const searchParts = [
        product.id,
        product.title,
        product.description,
        product.section,
        product.status,
        product.governance?.authority,
        ...(product.tags || []),
    ];
    card.dataset.search = normalizedSearch(searchParts.join(' '));
    card.append(icon, copy, meta, actionButton);
    return card;
}

function createEmptyState(record) {
    const empty = createElement(
        record.document,
        'p',
        'wsp-empty',
        'Nessun prodotto corrisponde alla ricerca.',
    );
    empty.hidden = true;
    return empty;
}

function renderProductCards(record) {
    const focusedProduct = record.document.activeElement
        ?.closest?.('.wsp-product')
        ?.dataset?.productId || null;

    for (const section of platformSections()) {
        const ui = record.sectionUi.get(section.id);
        if (!ui?.grid) continue;
        const products = record.snapshot.products.filter(product => product.section === section.id);
        const fragment = record.document.createDocumentFragment();
        for (const product of products) fragment.appendChild(createProductCard(record, product));
        const empty = createEmptyState(record);
        fragment.appendChild(empty);
        ui.grid.replaceChildren(fragment);
        ui.empty = empty;
        ui.total = products.length;
    }

    applyProductSearch(record);
    if (focusedProduct) {
        record.panel.querySelector(
            '.wsp-product[data-product-id="' + focusedProduct + '"] .wsp-product__action',
        )?.focus();
    }
}

function applyProductSearch(record) {
    const query = normalizedSearch(record.searchInput.value);
    let firstMatchingSection = null;
    let activeMatches = 0;

    for (const section of platformSections()) {
        const ui = record.sectionUi.get(section.id);
        if (!ui?.grid) continue;
        let matches = 0;
        for (const card of ui.grid.querySelectorAll('.wsp-product')) {
            const terms = query.split(/\s+/).filter(Boolean);
            const words = card.dataset.search.split(/[^a-z0-9]+/).filter(Boolean);
            const match = !query || terms.every(term => words.some(word => word.startsWith(term)));
            card.hidden = !match;
            if (match) matches += 1;
        }
        if (ui.empty) ui.empty.hidden = matches > 0;
        if (ui.count) {
            ui.count.textContent = query
                ? matches + '/' + ui.total + ' prodotti'
                : ui.total + (ui.total === 1 ? ' prodotto' : ' prodotti');
        }
        if (matches > 0 && !firstMatchingSection) firstMatchingSection = section.id;
        if (record.activeSection === section.id) activeMatches = matches;
    }

    if (query && firstMatchingSection
        && (record.activeSection === 'summary' || activeMatches === 0)) {
        selectWorkspaceSection(record, firstMatchingSection, {
            source: 'search',
            notify: true,
        });
    }
}

function setKpi(kpi, value, label) {
    kpi.querySelector('strong').textContent = value;
    kpi.querySelector('span').textContent = label;
}

function contextTerritorialCount(record) {
    const explicit = finite(record.context.territorialCount);
    if (explicit !== null) return explicit;
    const territorial = record.context.state?.territorial_intelligence;
    return finite(territorial?.event_count, Array.isArray(territorial?.events) ? territorial.events.length : 0);
}

function temporalRole(record) {
    const target = normalizeTargetMs(record.context.targetMs);
    const mode = normalizeSnapshotMode(record.context.mode, target);
    if (target === null) {
        return mode === 'forecast'
            ? 'Previsione'
            : mode === 'nowcast' ? 'Nowcast' : mode === 'all' ? 'Storico' : 'Tempo reale';
    }
    const deltaMinutes = Math.round((target - Date.now()) / 60000);
    if (Math.abs(deltaMinutes) <= 5) return 'Tempo reale';
    if (deltaMinutes > 0) return mode === 'nowcast' ? 'Nowcast' : 'Previsione';
    return 'Storico';
}

function updateOverview(record) {
    const state = record.context.state || {};
    const fusion = state?.live?.total_fusion || {};
    const fresh = finite(fusion.fresh_source_count, 0);
    const total = finite(fusion.source_count, 0);
    const available = finite(record.snapshot.summary?.availableCount, 0);
    const productCount = finite(record.snapshot.summary?.productCount, 0);
    const role = temporalRole(record);

    setKpi(record.kpis.sources, formatNumber(fresh) + '/' + formatNumber(total), 'fonti live');
    setKpi(record.kpis.products, formatNumber(available) + '/' + formatNumber(productCount), 'prodotti disponibili');
    setKpi(record.kpis.events, formatNumber(contextTerritorialCount(record)), 'eventi territoriali');
    setKpi(record.kpis.context, role, 'contesto temporale');

    for (const section of record.snapshot.sections || []) {
        const ui = record.sectionUi.get(section.id);
        if (ui?.count && !normalizedSearch(record.searchInput.value)) {
            ui.count.textContent = section.productCount
                + (section.productCount === 1 ? ' prodotto' : ' prodotti');
        }
    }
}

function briefingFocus(record, item) {
    if (!Number.isFinite(item?.lat) || !Number.isFinite(item?.lon)) return;
    const descriptor = {
        type: 'focus',
        target: 'map',
        value: item.id,
        source: 'jarvis-briefing',
    };
    callBridge(record, 'onFocus', item, descriptor, null);
    dispatchWorkspaceEvent(record, 'focus', descriptor, {id: item.id, section: item.phase});
}

function createBriefingItem(record, item) {
    const focusable = Number.isFinite(item?.lat) && Number.isFinite(item?.lon);
    const card = createElement(
        record.document,
        focusable ? 'button' : 'article',
        'wsp-briefing-item',
    );
    if (focusable) {
        card.type = 'button';
        card.setAttribute('aria-label', item.title + '. Mostra sulla mappa');
        card.addEventListener('click', () => briefingFocus(record, item), {
            signal: record.abort.signal,
        });
    }
    card.dataset.severity = item.severity || 'info';
    const header = createElement(record.document, 'header');
    header.append(
        createElement(record.document, 'span', 'wsp-briefing-item__icon', item.icon || '◆'),
        createElement(record.document, 'b', '', item.title),
    );
    if (Number.isFinite(item.confidence)) {
        header.appendChild(createElement(
            record.document,
            'small',
            '',
            Math.round(item.confidence * 100) + '%',
        ));
    }
    card.append(
        header,
        createElement(record.document, 'p', '', item.description),
        createElement(
            record.document,
            'footer',
            '',
            item.timeLabel + (focusable ? ' · mostra sulla mappa' : ''),
        ),
    );
    return card;
}

function renderBriefingLane(record, container, items, emptyText) {
    const fragment = record.document.createDocumentFragment();
    if (!items.length) {
        fragment.appendChild(createElement(record.document, 'p', 'wsp-briefing-empty', emptyText));
    } else {
        items.forEach(item => fragment.appendChild(createBriefingItem(record, item)));
    }
    container.replaceChildren(fragment);
}

function scenarioTimes(record) {
    return (record.context.state?.synoptic_scenario?.timesteps || [])
        .map(step => normalizeTargetMs(Date.parse(step?.valid_utc || '')))
        .filter(Number.isFinite)
        .sort((a, b) => a - b);
}

function stopBriefingPlayback(record) {
    if (record.playTimer) clearInterval(record.playTimer);
    record.playTimer = null;
    record.playing = false;
    if (record.playButton) {
        record.playButton.classList.remove('is-playing');
        record.playButton.textContent = '▶ Evoluzione';
        record.playButton.setAttribute('aria-pressed', 'false');
    }
}

function nextScenarioTime(record) {
    const target = normalizeTargetMs(record.context.targetMs) ?? Date.now();
    return scenarioTimes(record).find(time => time > target + 60 * 1000) || null;
}

function toggleBriefingPlayback(record) {
    if (record.playing) {
        stopBriefingPlayback(record);
        return;
    }
    if (!nextScenarioTime(record)) return;
    record.playing = true;
    record.playButton.classList.add('is-playing');
    record.playButton.textContent = '■ Pausa';
    record.playButton.setAttribute('aria-pressed', 'true');
    const advance = () => {
        const targetMs = nextScenarioTime(record);
        if (!targetMs) {
            stopBriefingPlayback(record);
            return;
        }
        const descriptor = {
            type: 'timeline',
            target: 'intelligence',
            value: 'playback',
            source: 'jarvis-briefing',
        };
        callBridge(record, 'onTimeline', {intent: 'playback', targetMs}, descriptor, null);
    };
    advance();
    record.playTimer = setInterval(advance, 1800);
}

function setExpertOpen(record, open, options = {}) {
    if (!record.jarvis || !record.expert) return false;
    const desired = Boolean(open);
    const changed = record.expertOpen !== desired;
    if (changed) {
        record.expertOpen = desired;
        record.jarvis.hidden = desired;
        record.expert.hidden = !desired;
        record.panel.classList.toggle('is-expert-open', desired);
        record.expertToggle?.setAttribute('aria-expanded', String(desired));
        window.WSWorkspace?.store?.setExtension?.('interface', {expertOpen: desired});
    }
    if (desired) {
        if (changed) renderProductCards(record);
        if (options.focus !== false) record.expertBack?.focus();
    } else if (options.focus) {
        record.expertToggle?.focus();
    }
    return true;
}

function renderJarvisBriefing(record) {
    if (!record.jarvisRefs) return null;
    const briefing = composeWeatherBriefing({
        state: record.context.state,
        territorialDocument: record.context.territorialDocument,
        targetMs: record.context.targetMs,
        mode: record.context.mode,
        nowMs: Date.now(),
    });
    record.briefing = briefing;
    record.panel.dataset.jarvisTone = briefing.tone;
    record.headingSubtitle.textContent = briefing.contextLabel;
    record.jarvisRefs.eyebrow.textContent = briefing.mode === 'live'
        ? 'SITUAZIONE ADESSO'
        : briefing.mode === 'history' ? 'RICOSTRUZIONE' : 'SCENARIO ALLA SCADENZA';
    record.jarvisRefs.headline.textContent = briefing.headline;
    record.jarvisRefs.summary.textContent = briefing.summary;
    record.jarvisRefs.confidence.textContent = Number.isFinite(briefing.confidence)
        ? 'Confidenza ' + briefing.confidenceLabel + ' · ' + Math.round(briefing.confidence * 100) + '%'
        : briefing.trustLabel || 'Confidenza non pubblicata';
    record.jarvisRefs.confidence.classList.toggle('is-muted', !Number.isFinite(briefing.confidence));
    record.jarvisRefs.sources.textContent = briefing.sourceSummary;
    record.jarvisRefs.uncertainty.textContent = briefing.uncertainty;
    record.jarvisRefs.evidence.replaceChildren(
        ...(briefing.proof || []).map(proof =>
            createElement(record.document, 'span', '', proof),
        ),
        ...briefing.evidence.map(source =>
            createElement(record.document, 'span', '', 'Fonte · ' + source),
        ),
    );
    renderBriefingLane(
        record,
        record.jarvisRefs.now,
        briefing.now,
        briefing.mode === 'live'
            ? 'Nessun altro segnale prioritario in corso.'
            : 'Nessun segnale prioritario attivo a questa scadenza.',
    );
    renderBriefingLane(
        record,
        record.jarvisRefs.next,
        briefing.next,
        'Nessun cambiamento rilevante entro la copertura disponibile.',
    );

    const fusion = record.context.state?.live?.total_fusion || {};
    const fresh = finite(fusion.fresh_source_count, 0);
    const total = finite(fusion.source_count, 0);
    const healthText = record.health.querySelector('span');
    healthText.textContent = briefing.updatedLabel;
    record.health.classList.toggle('is-degraded', !briefing.available || total <= 0 || fresh < total);
    record.health.setAttribute('aria-label', briefing.updatedLabel);
    record.timeRole.textContent = briefing.contextLabel.replace('Sicilia · ', '');

    const hasNext = Boolean(nextScenarioTime(record));
    record.playButton.disabled = !briefing.available || !hasNext;
    if (!hasNext && record.playing) stopBriefingPlayback(record);
    return briefing;
}

function renderWorkspace(record) {
    record.snapshot = safeSnapshot(record);
    updateOverview(record);
    renderJarvisBriefing(record);
    if (record.expertOpen) renderProductCards(record);
    return record.snapshot;
}

function createSectionHead(record, definition) {
    const head = createElement(record.document, 'header', 'wsp-section-head');
    const copy = createElement(record.document, 'div');
    copy.append(
        createElement(record.document, 'b', '', definition.label),
        createElement(record.document, 'small', '', definition.description),
    );
    const count = createElement(record.document, 'span', '', '0 prodotti');
    head.append(copy, count);
    return {head, count};
}

function createKpi(record, label) {
    const kpi = createElement(record.document, 'div', 'wsp-kpi');
    kpi.append(
        createElement(record.document, 'strong', '', '—'),
        createElement(record.document, 'span', '', label),
    );
    return kpi;
}

function restoreFocus(record) {
    const target = record.returnFocus;
    if (!target?.isConnected || typeof target.focus !== 'function') return;
    const run = () => target.focus({preventScroll: true});
    if (record.document.defaultView?.requestAnimationFrame) {
        record.document.defaultView.requestAnimationFrame(run);
    } else {
        setTimeout(run, 0);
    }
}

function panelIsAvailable(record) {
    return record.panel.isConnected
        && !record.panel.hidden
        && record.panel.getAttribute('aria-hidden') !== 'true';
}

function installKeyboard(record) {
    const signal = record.abort.signal;
    record.nav.addEventListener('keydown', event => {
        const tabs = Array.from(record.nav.querySelectorAll('[role="tab"]'));
        const current = tabs.indexOf(event.target);
        if (current < 0) return;
        let next = null;
        if (event.key === 'ArrowRight') next = (current + 1) % tabs.length;
        else if (event.key === 'ArrowLeft') next = (current - 1 + tabs.length) % tabs.length;
        else if (event.key === 'Home') next = 0;
        else if (event.key === 'End') next = tabs.length - 1;
        if (next === null) return;
        event.preventDefault();
        const id = tabs[next].dataset.section;
        selectWorkspaceSection(record, id, {focus: true, source: 'keyboard'});
    }, {signal});

    record.document.addEventListener('keydown', event => {
        if (!panelIsAvailable(record)) return;
        const target = event.target;
        const typing = target instanceof record.document.defaultView.HTMLElement
            && Boolean(target.closest('input, textarea, select, [contenteditable="true"]'));
        const searchShortcut = (event.key === '/' && !typing)
            || ((event.ctrlKey || event.metaKey) && event.key.toLowerCase() === 'k');
        if (searchShortcut) {
            event.preventDefault();
            event.stopPropagation();
            setExpertOpen(record, true, {focus: false});
            record.searchInput.focus();
            record.searchInput.select();
            return;
        }
        if (event.key !== 'Escape') return;
        event.preventDefault();
        if (record.searchInput.value) {
            record.searchInput.value = '';
            applyProductSearch(record);
            record.searchInput.focus();
            return;
        }
        if (record.expertOpen) {
            selectWorkspaceSection(record, 'summary', {source: 'escape'});
            record.expertToggle.focus();
            return;
        }
        record.close.click();
    }, {signal});
}

function claimStationParameter(record) {
    const select = record.stationParameter;
    const target = record.stationParameterTarget;
    if (!select || !target || select.parentNode === target) return;
    select.classList.add('wsp-station-parameter__select');
    target.appendChild(select);
}

function releaseStationParameter(record) {
    const select = record.stationParameter;
    const origin = record.stationParameterOrigin;
    if (!select || !origin?.parent?.isConnected) return;
    select.classList.remove('wsp-station-parameter__select');
    const next = origin.next?.parentNode === origin.parent ? origin.next : null;
    origin.parent.insertBefore(select, next);
}

function publicApi(record) {
    return Object.freeze({
        selectSection(sectionId) {
            return selectWorkspaceSection(record, sectionId, {focus: false, source: 'api'});
        },
        refresh() {
            return renderWorkspace(record);
        },
        update(context) {
            return updateRecordContext(record, context);
        },
        getSnapshot() {
            return record.snapshot;
        },
        getBriefing() {
            return record.briefing || null;
        },
        isExpertOpen() {
            return record.expertOpen;
        },
        stopEvolution() {
            stopBriefingPlayback(record);
        },
        claimStationParameter() {
            claimStationParameter(record);
        },
        releaseStationParameter() {
            releaseStationParameter(record);
        },
        destroy() {
            stopBriefingPlayback(record);
            releaseStationParameter(record);
            record.abort.abort();
            mounts.delete(record.panel);
            if (activeWorkspace === record) activeWorkspace = null;
        },
    });
}

function updateRecordContext(record, context = {}) {
    if (Object.prototype.hasOwnProperty.call(context, 'state')) {
        record.context.state = context.state || {};
    }
    if (Object.prototype.hasOwnProperty.call(context, 'mode')) {
        record.context.mode = context.mode;
    }
    if (Object.prototype.hasOwnProperty.call(context, 'targetMs')) {
        record.context.targetMs = context.targetMs;
    }
    if (Object.prototype.hasOwnProperty.call(context, 'territorialCount')) {
        record.context.territorialCount = context.territorialCount;
    }
    if (Object.prototype.hasOwnProperty.call(context, 'territorialDocument')) {
        record.context.territorialDocument = context.territorialDocument || null;
    }
    return renderWorkspace(record);
}

export function mountIntelligenceWorkspace(panel, {
    state = {},
    content = {},
    onMode = null,
    onTimeline = null,
    onSection = null,
    onFocus = null,
} = {}) {
    if (!panel || panel.nodeType !== 1) {
        throw new TypeError('mountIntelligenceWorkspace requires a panel element');
    }

    const previous = mounts.get(panel);
    if (previous) {
        stopBriefingPlayback(previous);
        previous.abort.abort();
    }

    const documentRef = panel.ownerDocument;
    const activeElement = documentRef.activeElement;
    const returnFocus = previous?.returnFocus?.isConnected
        ? previous.returnFocus
        : (activeElement && !panel.contains(activeElement) ? activeElement : null);
    const stationParameter = content.stationParameter || previous?.stationParameter || null;
    const stationParameterOrigin = previous?.stationParameterOrigin || (
        stationParameter?.parentNode && !panel.contains(stationParameter)
            ? {parent: stationParameter.parentNode, next: stationParameter.nextSibling}
            : null
    );
    const operationalMarkup = buildOperationalWorkspaceMarkup(content);
    panel.replaceChildren();

    const record = {
        panel,
        document: documentRef,
        abort: new AbortController(),
        callbacks: {onMode, onTimeline, onSection, onFocus},
        context: {
            state: state || {},
            mode: 'live',
            targetMs: Date.now(),
            territorialCount: undefined,
            territorialDocument: null,
        },
        snapshot: null,
        sectionUi: new Map(),
        activeSection: 'summary',
        expertOpen: false,
        playing: false,
        playTimer: null,
        returnFocus,
        stationParameter,
        stationParameterOrigin,
        stationParameterTarget: null,
    };
    const uid = 'wsp-' + (++workspaceSequence);
    panel.classList.add('wsp-workspace');
    panel.setAttribute('aria-label', 'Weather Sicily Intelligence');

    const header = createElement(documentRef, 'header', 'wsp-header');
    const heading = createElement(documentRef, 'div', 'wsp-heading');
    const title = createElement(documentRef, 'b', '', 'WS Intelligence');
    const subtitle = createElement(documentRef, 'small', '', 'Sicilia · ORA');
    heading.append(title, subtitle);
    title.id = uid + '-title';
    panel.setAttribute('aria-labelledby', title.id);

    const brand = createElement(documentRef, 'span', 'wsp-brandmark', 'WS');
    brand.setAttribute('aria-hidden', 'true');
    const health = createElement(documentRef, 'div', 'wsp-health');
    health.setAttribute('role', 'status');
    health.setAttribute('aria-live', 'polite');
    health.append(
        createElement(documentRef, 'i'),
        createElement(documentRef, 'span', '', 'Verifica piattaforma…'),
    );
    const headActions = createElement(documentRef, 'div', 'wsp-head-actions');
    const close = createElement(documentRef, 'button', '', '×');
    close.id = 'wsi-close';
    close.addEventListener('click', () => {
        panel.hidden = true;
        panel.setAttribute('aria-hidden', 'true');
    }, {signal: record.abort.signal});
    close.type = 'button';
    close.setAttribute('aria-label', 'Chiudi Weather Sicily Intelligence');
    headActions.append(health, close);
    header.prepend(brand, heading);
    header.appendChild(headActions);

    const handle = createElement(documentRef, 'span', 'wsp-workspace__handle');
    handle.setAttribute('aria-hidden', 'true');
    const command = createElement(documentRef, 'label', 'wsp-command');
    command.setAttribute('aria-label', 'Cerca prodotti Weather Sicily');
    const searchIcon = createElement(documentRef, 'span', '', '⌕');
    searchIcon.setAttribute('aria-hidden', 'true');
    const searchInput = createElement(documentRef, 'input');
    searchInput.type = 'search';
    searchInput.placeholder = 'Cerca dati, modelli, tracking…';
    searchInput.setAttribute('aria-label', 'Cerca prodotti e sistemi');
    searchInput.autocomplete = 'off';
    const shortcut = createElement(documentRef, 'kbd', '', '/');
    shortcut.setAttribute('aria-hidden', 'true');
    command.append(searchIcon, searchInput, shortcut);

    const nav = createElement(documentRef, 'nav', 'wsp-nav');
    nav.setAttribute('role', 'tablist');
    nav.setAttribute('aria-label', 'Sezioni Weather Sicily Intelligence');
    const views = createElement(documentRef, 'div', 'wsp-views');
    const definitions = [SUMMARY_SECTION, ...platformSections()];

    for (const definition of definitions) {
        const sectionId = definition.id;
        const label = TAB_LABELS[sectionId] || definition.label;
        const tab = createElement(documentRef, 'button');
        tab.type = 'button';
        tab.id = uid + '-tab-' + sectionId;
        tab.dataset.section = sectionId;
        tab.setAttribute('role', 'tab');
        tab.setAttribute('aria-controls', uid + '-view-' + sectionId);
        tab.setAttribute('aria-selected', sectionId === 'summary' ? 'true' : 'false');
        tab.tabIndex = sectionId === 'summary' ? 0 : -1;
        const tabIcon = createElement(documentRef, 'span', '', SECTION_ICONS[sectionId] || '•');
        tabIcon.setAttribute('aria-hidden', 'true');
        tab.append(tabIcon, createElement(documentRef, 'b', '', label));
        tab.addEventListener('click', () => {
            selectWorkspaceSection(record, sectionId, {source: 'tab'});
        }, {signal: record.abort.signal});
        nav.appendChild(tab);

        const view = createElement(documentRef, 'section', 'wsp-view');
        view.id = uid + '-view-' + sectionId;
        view.dataset.section = sectionId;
        view.setAttribute('role', 'tabpanel');
        view.setAttribute('aria-labelledby', tab.id);
        view.tabIndex = 0;
        view.hidden = sectionId !== 'summary';

        const sectionDefinition = sectionId === 'summary'
            ? SUMMARY_SECTION
            : {...definition, label};
        const sectionHead = createSectionHead(record, sectionDefinition);
        view.appendChild(sectionHead.head);
        record.sectionUi.set(sectionId, {
            tab,
            view,
            count: sectionHead.count,
            grid: null,
            empty: null,
            total: 0,
        });
        views.appendChild(view);
    }

    const summaryUi = record.sectionUi.get('summary');
    summaryUi.count.textContent = 'quadro unificato';
    const kpis = createElement(documentRef, 'div', 'wsp-kpis');
    record.kpis = {
        sources: createKpi(record, 'fonti live'),
        products: createKpi(record, 'prodotti disponibili'),
        events: createKpi(record, 'eventi territoriali'),
        context: createKpi(record, 'contesto temporale'),
    };
    kpis.append(
        record.kpis.sources,
        record.kpis.products,
        record.kpis.events,
        record.kpis.context,
    );
    const operational = {
        now: markupNode(documentRef, operationalMarkup.now),
        statusline: markupNode(documentRef, operationalMarkup.statusline),
        v3: markupNode(documentRef, operationalMarkup.wsnow),
        modes: markupNode(documentRef, operationalMarkup.modes),
        nearMe: markupNode(documentRef, operationalMarkup.nearMe),
        timeline: markupNode(documentRef, operationalMarkup.timeline),
        local: markupNode(documentRef, operationalMarkup.local),
        attention: markupNode(documentRef, operationalMarkup.attention),
        mapOptions: markupNode(documentRef, operationalMarkup.mapOptions),
        details: operationalMarkup.technicalDetails.map(markup => markupNode(documentRef, markup)),
        footer: markupNode(documentRef, operationalMarkup.footer),
    };
    const overview = createElement(documentRef, 'div', 'wsp-overview-grid');
    [
        operational.now,
        operational.statusline,
        operational.modes,
        operational.nearMe,
        operational.attention,
    ].forEach(node => overview.appendChild(node));
    summaryUi.view.append(kpis, overview);

    for (const section of platformSections()) {
        const ui = record.sectionUi.get(section.id);
        if (section.id === 'system') {
            ui.view.appendChild(createElement(
                documentRef,
                'p',
                'wsp-system-note',
                'La vista riporta soltanto prodotti operativi e le relative fonti.',
            ));
        }
        if (section.id === 'live' && stationParameter) {
            const control = createElement(documentRef, 'section', 'wsp-station-parameter');
            const copy = createElement(documentRef, 'div');
            copy.append(
                createElement(documentRef, 'b', '', 'Dato stazioni'),
                createElement(documentRef, 'small', '', 'Scegli il parametro osservato sulla rete live.'),
            );
            control.appendChild(copy);
            record.stationParameterTarget = control;
            claimStationParameter(record);
            ui.view.appendChild(control);
        }
        const grid = createElement(documentRef, 'div', 'wsp-product-grid');
        ui.grid = grid;
        ui.view.appendChild(grid);
    }

    record.sectionUi.get('forecast').view.appendChild(operational.mapOptions);
    const systemUi = record.sectionUi.get('system');
    const systemGrid = systemUi.grid;
    systemUi.view.insertBefore(operational.v3, systemGrid);
    for (const details of operational.details) systemUi.view.insertBefore(details, systemGrid);
    systemUi.view.appendChild(operational.footer);

    const timebar = createElement(documentRef, 'div', 'wsp-timebar');
    const timeTop = createElement(documentRef, 'div', 'wsp-timebar__top');
    timeTop.append(
        createElement(documentRef, 'b', '', 'Quando'),
        createElement(documentRef, 'span', 'wsp-timebar__role', 'ORA'),
    );
    record.timeRole = timeTop.querySelector('.wsp-timebar__role');
    timebar.append(timeTop, operational.timeline);

    const jarvis = createElement(documentRef, 'section', 'wsp-jarvis');
    jarvis.setAttribute('aria-label', 'Briefing meteorologico');

    const hero = createElement(documentRef, 'section', 'wsp-jarvis-hero');
    const eyebrow = createElement(documentRef, 'span', 'wsp-jarvis-hero__eyebrow', 'SITUAZIONE ADESSO');
    const headline = createElement(documentRef, 'h2', '', 'Analisi in corso…');
    const heroSummary = createElement(documentRef, 'p', '', 'Weather Sicily sta incrociando osservazioni e scenari.');
    const trust = createElement(documentRef, 'div', 'wsp-jarvis-trust');
    const confidence = createElement(documentRef, 'strong', '', 'Confidenza in verifica');
    const sources = createElement(documentRef, 'span', '', 'Fonti in verifica');
    trust.append(confidence, sources);
    hero.append(eyebrow, headline, heroSummary, trust);

    const lanes = createElement(documentRef, 'div', 'wsp-briefing-lanes');
    const nowLane = createElement(documentRef, 'section', 'wsp-briefing-lane');
    const nowHead = createElement(documentRef, 'header');
    nowHead.append(
        createElement(documentRef, 'span', '', '●'),
        createElement(documentRef, 'b', '', 'Adesso'),
    );
    const nowStories = createElement(documentRef, 'div', 'wsp-briefing-stories');
    nowLane.append(nowHead, nowStories);

    const nextLane = createElement(documentRef, 'section', 'wsp-briefing-lane');
    const nextHead = createElement(documentRef, 'header');
    nextHead.append(
        createElement(documentRef, 'span', '', '↗'),
        createElement(documentRef, 'b', '', 'Prossime ore'),
    );
    const nextStories = createElement(documentRef, 'div', 'wsp-briefing-stories');
    nextLane.append(nextHead, nextStories);
    lanes.append(nowLane, nextLane);

    const evidenceDetails = createElement(documentRef, 'details', 'wsp-jarvis-evidence');
    evidenceDetails.appendChild(createElement(documentRef, 'summary', '', 'Perché lo dice'));
    const evidenceBody = createElement(documentRef, 'div', 'wsp-jarvis-evidence__body');
    const uncertainty = createElement(documentRef, 'p', '', 'Verifica delle evidenze in corso.');
    const evidence = createElement(documentRef, 'div', 'wsp-jarvis-evidence__sources');
    evidenceBody.append(uncertainty, evidence);
    evidenceDetails.appendChild(evidenceBody);

    const actions = createElement(documentRef, 'div', 'wsp-jarvis-actions');
    const playButton = createElement(documentRef, 'button', 'wsp-jarvis-action', '▶ Evoluzione');
    playButton.type = 'button';
    playButton.setAttribute('aria-pressed', 'false');
    const locateButton = createElement(documentRef, 'button', 'wsp-jarvis-action', '📍 Intorno a me');
    locateButton.type = 'button';
    const expertToggle = createElement(documentRef, 'button', 'wsp-jarvis-action is-secondary', 'Dati e livelli');
    expertToggle.type = 'button';
    expertToggle.setAttribute('aria-expanded', 'false');
    actions.append(playButton, locateButton, expertToggle);

    jarvis.append(hero, lanes, timebar, evidenceDetails, operational.local, actions);

    const expert = createElement(documentRef, 'section', 'wsp-expert');
    expert.hidden = true;
    expert.setAttribute('aria-label', 'Dati e livelli tecnici');
    const expertHead = createElement(documentRef, 'header', 'wsp-expert__head');
    const expertBack = createElement(documentRef, 'button', '', '← Briefing');
    expertBack.type = 'button';
    const expertCopy = createElement(documentRef, 'div');
    expertCopy.append(
        createElement(documentRef, 'b', '', 'Dati e livelli'),
        createElement(documentRef, 'small', '', 'Controlli tecnici, fonti e diagnostica'),
    );
    expertHead.append(expertBack, expertCopy);
    expert.append(expertHead, command, nav, views);

    const fragment = documentRef.createDocumentFragment();
    fragment.append(handle, header, jarvis, expert);
    panel.appendChild(fragment);

    record.headingSubtitle = subtitle;
    record.health = health;
    record.close = close;
    record.searchInput = searchInput;
    record.nav = nav;
    record.jarvis = jarvis;
    record.expert = expert;
    record.expertBack = expertBack;
    record.expertToggle = expertToggle;
    record.playButton = playButton;
    record.jarvisRefs = {
        eyebrow,
        headline,
        summary: heroSummary,
        confidence,
        sources,
        now: nowStories,
        next: nextStories,
        uncertainty,
        evidence,
    };
    record.api = publicApi(record);

    searchInput.addEventListener('input', () => applyProductSearch(record), {
        signal: record.abort.signal,
    });
    playButton.addEventListener('click', () => toggleBriefingPlayback(record), {
        signal: record.abort.signal,
    });
    locateButton.addEventListener('click', () => operational.nearMe.click(), {
        signal: record.abort.signal,
    });
    expertToggle.addEventListener('click', () => {
        const section = record.activeSection === 'summary' ? 'live' : record.activeSection;
        selectWorkspaceSection(record, section, {source: 'expert-toggle'});
    }, {signal: record.abort.signal});
    expertBack.addEventListener('click', () => {
        selectWorkspaceSection(record, 'summary', {source: 'expert-back'});
        expertToggle.focus();
    }, {signal: record.abort.signal});
    close.addEventListener('click', () => {
        stopBriefingPlayback(record);
        restoreFocus(record);
    }, {signal: record.abort.signal});
    installKeyboard(record);

    mounts.set(panel, record);
    activeWorkspace = record;
    renderWorkspace(record);
    return record.api;
}

export function updateIntelligenceWorkspaceContext({
    state,
    mode,
    targetMs,
    territorialCount,
    territorialDocument,
} = {}) {
    if (!activeWorkspace) return null;
    return updateRecordContext(activeWorkspace, {
        ...(state !== undefined ? {state} : {}),
        ...(mode !== undefined ? {mode} : {}),
        ...(targetMs !== undefined ? {targetMs} : {}),
        ...(territorialCount !== undefined ? {territorialCount} : {}),
        ...(territorialDocument !== undefined ? {territorialDocument} : {}),
    });
}
